// F-086 — Compteurs et consommations : relevés datés, consommations calculées, exceptions et alertes signalées.
import { useState } from "react";
import { post, put, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, today } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Empty, ErrorBox, FileDrop, Input, Loading, Modal, Notice, PageHeader, Select, StatusChip, Table, Textarea, fieldError } from "@core/ui";
import { BuildingSelect, DocLink, PropertySelect, orgApi, required, useAction, useCatalog, useRefs } from "./shared";

type Period = { from_reading: string; to_reading: string; from: string; to: string; days: number; consumption_milli: number | null; daily_milli: number | null; status: string; alert: string | null };
type Reading = { id: string; read_on: string; value_milli: number; value: string; kind: string; kind_label: string; photo_document_id: string | null; note: string; history: { value_milli: number; motif: string }[] };
type Meter = {
  id: string;
  property_id: string | null;
  property_label: string | null;
  building_id: string | null;
  building_label: string | null;
  kind: string;
  kind_label: string;
  unit: string;
  unit_label: string;
  label: string;
  serial_number: string;
  location: string;
  active: boolean;
  last_reading: { read_on: string; value: string; kind: string } | null;
  readings_count: number;
  exceptions: number;
  alerts: number;
  last_period: Period | null;
  readings?: Reading[];
  periods?: Period[];
  notes?: string[];
};

const qty = (milli: number | null | undefined, unit: string) => (milli === null || milli === undefined ? "—" : `${(milli / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 3 })} ${unit}`);

const PERIOD_STATUS: Record<string, [string, "conforme" | "a_confirmer" | "anomalie" | "info" | "non_concluant"]> = {
  calcule: ["Calculée", "conforme"],
  estime: ["Relevé estimé, à confirmer", "a_confirmer"],
  meme_jour: ["Relevés du même jour", "info"],
  index_en_baisse: ["Index en baisse : à vérifier", "anomalie"],
  remplacement: ["Changement de compteur : non calculée", "non_concluant"],
};

export function MetersPage() {
  const org = useOrg();
  const refs = useRefs();
  const [building, setBuilding] = useState("");
  const [property, setProperty] = useState("");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Immeubles"
        title="Compteurs et consommations"
        intro="Relevés d’eau, d’électricité, de gaz ou de chauffage, par logement ou pour les parties communes. Bailivo calcule les consommations entre deux relevés et signale ce qui mérite une vérification."
      />
      <div className="finances-filtres">
        <BuildingSelect refs={refs.data} value={building} onChange={(v) => { setBuilding(v); setProperty(""); }} empty="Tous les immeubles" />
        <PropertySelect refs={refs.data} buildingId={building || undefined} value={property} onChange={setProperty} empty="Tous les logements" />
      </div>
      <MetersList orgId={org.orgId} propertyId={property || undefined} buildingId={building || undefined} />
    </div>
  );
}

export function PropertyMetersTab({ orgId, propertyId }: { orgId: string; propertyId: string }) {
  return <MetersList orgId={orgId} propertyId={propertyId} fixedProperty />;
}

function MetersList({ orgId, propertyId, buildingId, fixedProperty }: { orgId: string; propertyId?: string; buildingId?: string; fixedProperty?: boolean }) {
  const org = useOrg();
  const params = new URLSearchParams();
  if (propertyId) params.set("property_id", propertyId);
  if (buildingId) params.set("building_id", buildingId);
  const q = useGet<{ items: Meter[] }>(orgApi(orgId, `/utility-meters?${params}`));
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const canEdit = org.can("operate");
  return (
    <div className="stack">
      {canEdit ? (
        <div>
          <Button onClick={() => setAdding(true)}>Ajouter un compteur</Button>
        </div>
      ) : null}
      <Notice>Aucune connexion à un fournisseur d’énergie ou d’eau : les index sont ceux que vous relevez ou qui figurent sur vos factures.</Notice>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun compteur enregistré." /> : null}
      {q.data && q.data.items.length ? (
        <Table caption="Compteurs">
          <thead>
            <tr>
              <th>Compteur</th>
              <th>Emplacement</th>
              <th>Dernier relevé</th>
              <th>Dernière période</th>
              <th>À vérifier</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((m) => (
              <tr key={m.id}>
                <td>
                  {m.label || m.kind_label}
                  <span className="finances-sous">
                    {m.kind_label}
                    {m.serial_number ? ` · n° ${m.serial_number}` : ""}
                    {m.active ? "" : " · inactif"}
                  </span>
                </td>
                <td>
                  {m.property_label ?? `${m.building_label ?? ""} (compteur commun)`}
                  {m.location ? <span className="finances-sous">{m.location}</span> : null}
                </td>
                <td>{m.last_reading ? <>{`${m.last_reading.value} ${m.unit_label}`}<span className="finances-sous">le {date(m.last_reading.read_on)}</span></> : "Aucun"}</td>
                <td>{m.last_period ? <>{qty(m.last_period.consumption_milli, m.unit_label)}<span className="finances-sous">{m.last_period.days} jours</span></> : "—"}</td>
                <td>
                  <div className="row">
                    {m.alerts ? <Badge tone="attention">{m.alerts} écart{m.alerts > 1 ? "s" : ""} de consommation</Badge> : null}
                    {m.exceptions ? <Badge>{m.exceptions} exception{m.exceptions > 1 ? "s" : ""}</Badge> : null}
                    {!m.alerts && !m.exceptions ? <span className="muted small">Rien à signaler</span> : null}
                  </div>
                </td>
                <td>
                  <Button small variant="discret" onClick={() => setOpen(m.id)}>Relevés</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {adding ? <MeterModal orgId={orgId} presetProperty={fixedProperty ? propertyId : undefined} onClose={() => setAdding(false)} /> : null}
      {open ? <MeterDetail orgId={orgId} id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function MeterModal({ orgId, presetProperty, onClose }: { orgId: string; presetProperty?: string; onClose: () => void }) {
  const refs = useRefs(orgId);
  const catalog = useCatalog(orgId);
  const [f, setF] = useState({ property_id: presetProperty ?? "", building_id: "", kind: "eau_froide", label: "", serial_number: "", location: "" });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Nouveau compteur"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button busy={a.busy} onClick={async () => {
            const r = await a.run(() => post(orgApi(orgId, "/utility-meters"), { ...f, property_id: f.property_id || null, building_id: f.property_id ? null : f.building_id || null }), { success: "Compteur ajouté.", refresh: [orgApi(orgId, "/utility-meters")] });
            if (r) onClose();
          }}>Enregistrer</Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !fieldError(a.error, "property_id") ? a.error : null} />
        <Select label="Type" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={(catalog.data?.meter_kinds ?? []).map((k) => ({ value: k.code, label: `${k.label} (${k.unit === "m3" ? "m³" : k.unit === "kwh" ? "kWh" : "unités"})` }))} />
        {presetProperty ? null : (
          <div className="form-row">
            <PropertySelect refs={refs.data} value={f.property_id} error={fieldError(a.error, "property_id")} onChange={(v) => setF({ ...f, property_id: v })} empty="Compteur commun (immeuble)" />
            {!f.property_id ? <BuildingSelect refs={refs.data} value={f.building_id} onChange={(v) => setF({ ...f, building_id: v })} empty="Choisir…" /> : null}
          </div>
        )}
        <div className="form-row">
          <Input label="Nom" placeholder="Ex. Eau froide cuisine" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          <Input label="Numéro du compteur" value={f.serial_number} onChange={(e) => setF({ ...f, serial_number: e.target.value })} />
        </div>
        <Input label="Emplacement" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
      </div>
    </Modal>
  );
}

function MeterDetail({ orgId, id, onClose }: { orgId: string; id: string; onClose: () => void }) {
  const org = useOrg();
  const catalog = useCatalog(orgId);
  const q = useGet<Meter>(orgApi(orgId, `/utility-meters/${id}`));
  const [form, setForm] = useState({ read_on: today(), value: "", kind: "releve", note: "", photo_document_id: "" });
  const [correcting, setCorrecting] = useState<Reading | null>(null);
  const [replacing, setReplacing] = useState(false);
  const a = useAction();
  const photo = useAction();
  const m = q.data;
  const canEdit = org.can("operate");
  const refresh = [orgApi(orgId, "/utility-meters")];
  return (
    <Modal open onClose={onClose} title={m ? `${m.label || m.kind_label}${m.serial_number ? ` — n° ${m.serial_number}` : ""}` : "Compteur"}>
      <div className="stack">
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {m ? (
          <>
            <h3>Consommations</h3>
            {(m.periods ?? []).length === 0 ? <p className="muted small">Deux relevés au moins sont nécessaires.</p> : null}
            {(m.periods ?? []).length ? (
              <Table caption="Consommations entre relevés">
                <thead>
                  <tr>
                    <th>Période</th>
                    <th className="right">Consommation</th>
                    <th className="right">Par jour</th>
                    <th>Constat</th>
                  </tr>
                </thead>
                <tbody>
                  {(m.periods ?? []).map((p) => {
                    const [label, st] = PERIOD_STATUS[p.status] ?? [p.status, "info"];
                    return (
                      <tr key={`${p.from_reading}-${p.to_reading}`}>
                        <td>du {date(p.from)} au {date(p.to)}<span className="finances-sous">{p.days} jours</span></td>
                        <td className="right">{qty(p.consumption_milli, m.unit_label)}</td>
                        <td className="right">{qty(p.daily_milli, m.unit_label)}</td>
                        <td>
                          <div className="row">
                            <StatusChip status={st} label={label} />
                            {p.alert ? <StatusChip status="a_confirmer" label={p.alert === "hausse_inhabituelle" ? "Hausse inhabituelle, à vérifier" : "Baisse inhabituelle, à vérifier"} /> : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : null}
            <h3>Relevés</h3>
            <Table caption="Relevés">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="right">Index</th>
                  <th>Type</th>
                  <th>Note</th>
                  {canEdit ? <th><span className="sr-only">Actions</span></th> : null}
                </tr>
              </thead>
              <tbody>
                {(m.readings ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>{date(r.read_on)}</td>
                    <td className="right">{r.value} {m.unit_label}</td>
                    <td>{r.kind_label}</td>
                    <td>
                      {r.note}
                      {r.photo_document_id ? <> <DocLink id={r.photo_document_id} name="Photo" /></> : null}
                      {r.history.length ? <span className="finances-sous">Corrigé ({r.history.map((h) => `${(h.value_milli / 1000).toLocaleString("fr-FR")} : ${h.motif}`).join(" ; ")})</span> : null}
                    </td>
                    {canEdit ? (
                      <td>
                        <Button small variant="discret" onClick={() => setCorrecting(r)}>Corriger</Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
            {canEdit ? (
              <div className="stack-sm">
                <h3>Nouveau relevé</h3>
                <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
                <div className="form-row">
                  <Input label="Date" type="date" value={form.read_on} error={fieldError(a.error, "read_on")} onChange={(e) => setForm({ ...form, read_on: e.target.value })} />
                  <Input label={`Index (${m.unit_label})`} inputMode="decimal" placeholder="Ex. 131,9" value={form.value} error={fieldError(a.error, "value")} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                  <Select label="Type de relevé" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} options={(catalog.data?.reading_kinds ?? []).filter((k) => k.code !== "pose" && k.code !== "depose").map((k) => ({ value: k.code, label: k.label }))} />
                </div>
                <Input label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                <FileDrop
                  busy={photo.busy}
                  label={form.photo_document_id ? "Photo jointe — remplacer" : "Joindre une photo (facultatif)"}
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onFiles={async (files) => {
                    const fd = new FormData();
                    fd.append("file", files[0]);
                    const r = await photo.run(() => upload<{ document_id: string }>(orgApi(orgId, `/utility-meters/${id}/photo`), fd));
                    if (r) setForm({ ...form, photo_document_id: r.document_id });
                  }}
                />
                <div className="finances-actions">
                  <Button variant="secondaire" onClick={() => setReplacing(true)}>Changement de compteur</Button>
                  <Button
                    busy={a.busy}
                    onClick={async () => {
                      const missing = required([[!!form.read_on, "read_on", "Indiquez la date du relevé."]]);
                      if (missing) return a.setError(missing);
                      const r = await a.run(() => post(orgApi(orgId, `/utility-meters/${id}/readings`), { ...form, photo_document_id: form.photo_document_id || null }), { success: "Relevé enregistré.", refresh });
                      if (r) setForm({ read_on: today(), value: "", kind: "releve", note: "", photo_document_id: "" });
                    }}
                  >
                    Enregistrer le relevé
                  </Button>
                </div>
              </div>
            ) : null}
            <ul className="finances-formules">
              {(m.notes ?? []).map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </>
        ) : null}
        {correcting ? <CorrectModal orgId={orgId} meterId={id} reading={correcting} onClose={() => setCorrecting(null)} /> : null}
        {replacing ? <ReplaceModal orgId={orgId} meterId={id} onClose={() => setReplacing(false)} /> : null}
      </div>
    </Modal>
  );
}

function CorrectModal({ orgId, meterId, reading, onClose }: { orgId: string; meterId: string; reading: Reading; onClose: () => void }) {
  const [f, setF] = useState({ read_on: reading.read_on, value: reading.value, kind: reading.kind, note: reading.note, reason: "" });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Corriger un relevé"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button busy={a.busy} onClick={async () => {
            const r = await a.run(() => put(orgApi(orgId, `/utility-meters/${meterId}/readings/${reading.id}`), { ...f, photo_document_id: reading.photo_document_id }), { success: "Relevé corrigé ; l’ancienne valeur est conservée.", refresh: [orgApi(orgId, "/utility-meters")] });
            if (r) onClose();
          }}>Corriger</Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <div className="form-row">
          <Input label="Date" type="date" value={f.read_on} error={fieldError(a.error, "read_on")} onChange={(e) => setF({ ...f, read_on: e.target.value })} />
          <Input label="Index" inputMode="decimal" value={f.value} error={fieldError(a.error, "value")} onChange={(e) => setF({ ...f, value: e.target.value })} />
        </div>
        <Input label="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <Textarea label="Motif de la correction" required value={f.reason} error={fieldError(a.error, "reason")} onChange={(e) => setF({ ...f, reason: e.target.value })} />
      </div>
    </Modal>
  );
}

function ReplaceModal({ orgId, meterId, onClose }: { orgId: string; meterId: string; onClose: () => void }) {
  const [f, setF] = useState({ read_on: today(), old_final_value: "", new_initial_value: "", new_serial_number: "" });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Changement de compteur"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button busy={a.busy} onClick={async () => {
            const r = await a.run(() => post(orgApi(orgId, `/utility-meters/${meterId}/replacement`), f), { success: "Changement enregistré.", refresh: [orgApi(orgId, "/utility-meters")] });
            if (r) onClose();
          }}>Enregistrer</Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">La consommation de la période du changement n’est pas calculée ; le suivi reprend à partir de l’index de pose.</p>
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <Input label="Date du changement" type="date" value={f.read_on} error={fieldError(a.error, "read_on")} onChange={(e) => setF({ ...f, read_on: e.target.value })} />
        <div className="form-row">
          <Input label="Dernier index de l’ancien compteur" inputMode="decimal" value={f.old_final_value} error={fieldError(a.error, "old_final_value")} onChange={(e) => setF({ ...f, old_final_value: e.target.value })} />
          <Input label="Index de départ du nouveau" inputMode="decimal" value={f.new_initial_value} error={fieldError(a.error, "new_initial_value")} onChange={(e) => setF({ ...f, new_initial_value: e.target.value })} />
        </div>
        <Input label="Numéro du nouveau compteur" value={f.new_serial_number} onChange={(e) => setF({ ...f, new_serial_number: e.target.value })} />
      </div>
    </Modal>
  );
}
